import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlContainer, FormControl } from '@angular/forms';
import { DestroyableComponent } from '../../base/destroyable.component';
import { isControlOptional } from '../../utils/is-control-optional.util';

@Component({
    selector: 'tvcore-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    standalone: false
})
export class InputComponent extends DestroyableComponent implements OnInit {
	@Input()
	example?: string;
	@Input()
	label: string;
	@Input()
	labelInfo?: string;
	@Input()
	ltr: boolean = false;
	@Input()
	maxChar: number | null = null;
	@Input()
	name: string;
	@Input()
	placeholder: string;
	@Input()
	type: 'email' | 'hour' | 'number' | 'price' | 'tel' | 'text' | 'textarea' = 'text';
	@Input()
	showErrors: boolean = false;
	@Input()
	unit?: string;
	@Input()
	min?: number;
	@Input()
	max?: number;
	@Input()
	prefix: string;
	@Output()
	focusInput: EventEmitter<void> = new EventEmitter<void>();
	@Output()
	focusOut: EventEmitter<void> = new EventEmitter<void>();
	@ViewChild('prefixElement')
	prefixElement: any;

	control: FormControl | null;

	constructor(private controlContainer: ControlContainer) {
		super();
	}

	get charCount(): number {
		return this.control?.value?.length || 0;
	}

	get isOptional(): boolean {
		return isControlOptional(this.control);
	}

	ngOnInit() {
		this.control = this.controlContainer.control?.get(this.name) as FormControl;
		this.formatPrice();

		if (this.prefix) {
			requestAnimationFrame(() => {});
		}
	}

	input(event: KeyboardEvent) {
		switch (this.type) {
			case 'hour':
				return this.hourInput(event);
			case 'number':
				return this.numbersInput(event);
			case 'price':
				return this.numbersInput(event, true);
			default:
				return true;
		}
	}

	focusIn() {
		this.focusInput.next();
	}

	blur() {
		this.formatPrice();
		this.focusOut.next();
	}

	hasValue(): boolean {
		return !!this.control?.value?.toString();
	}

	hasUnit(): boolean {
		return !!this.unit;
	}

	hasError(name: string): boolean {
		return !!this.control?.hasError(name);
	}

	hasErrors(): boolean {
		if (this.showErrors) {
			return !!this.control?.errors;
		}

		return false;
	}

	private hourInput(event: KeyboardEvent) {
		const charCode = event.which ?? event.charCode;

		if (charCode < 48 || charCode > 58) {
			event.preventDefault();
			return false;
		}

		return true;
	}

	private formatPrice() {
		if (this.type === 'price' && this.control) {
			if (this.control.value >= 0) {
				this.control.setValue((+this.control.value).toFixed(2));
			} else {
				this.control.setValue(null);
			}
		}
	}

	private numbersInput(event: KeyboardEvent, allowComma: boolean = false) {
		const charCode = event.which ?? event.charCode;

		if (allowComma && (charCode === 44 || charCode === 46)) {
			return true;
		}

		if (charCode >= 48 && charCode <= 57) {
			return true;
		}

		event.preventDefault();
		return false;
	}
}
